/* global BigInt */
import React, { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from '../graphConfig';
import {
  NETWORKS, isAddr, fmt, resolveLockWallets, subgraphDelegations,
  readDelegation, readLockWallet, buildSteps, simulate,
} from '../lib/delegation';

function Copy({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="search-button" style={{ padding: '2px 10px', fontSize: 12 }}
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? 'Copied ✓' : 'Copy'}
    </button>
  );
}

function SimBadge({ sim, step }) {
  if (!sim) return null;
  if (sim === 'running') return <span className="notion-pill">simulating…</span>;
  if (sim.ok) return <span className="notion-pill" style={{ background: '#d1fadf', color: '#05603a' }}>✅ Would succeed{sim.gas ? ` · ~${sim.gas.toLocaleString()} gas` : ''}</span>;
  if (!step.ready) return <span className="notion-pill">⏳ Not ready: {step.notReady}</span>;
  return <span className="notion-pill" style={{ background: '#fee4e2', color: '#b42318' }}>❌ Would fail: {sim.reason}</span>;
}

function Steps({ net, lock, deleg, info, from }) {
  const n = NETWORKS[net];
  const steps = buildSteps(net, deleg, info);
  const [sims, setSims] = useState({});
  const runSim = async () => {
    setSims(Object.fromEntries(steps.map((s) => [s.key, 'running'])));
    for (const s of steps) {
      const r = await simulate(net, from, lock, s.data);
      setSims((p) => ({ ...p, [s.key]: r }));
    }
  };
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
        <button type="button" className="search-button" onClick={runSim} disabled={!isAddr(from)}>▶ Simulate all steps</button>
        <small>Live-state dry run (eth_call from {from ? `${from.slice(0, 6)}…${from.slice(-4)}` : 'your wallet'} → lock wallet on {n.short}). Nothing is sent. Not a guarantee — state can change before you sign.</small>
      </div>
      <div className="notion-steps">
        {steps.map((s, i) => (
          <div className="notion-step" key={s.key}>
            <div className="notion-step-num">{i + 1}</div>
            <div className="notion-step-body">
              <h4>{s.title} {!s.ready && !sims[s.key] && <span className="notion-pill">not yet</span>} <SimBadge sim={sims[s.key]} step={s} /></h4>
              <p><strong>What it does:</strong> {s.does}</p>
              <p><strong>From:</strong> <code className="notion-code">{from || 'your beneficiary wallet'}</code><br />
                <strong>To:</strong> <code className="notion-code">{lock}</code> <Copy text={lock} /> (the lock wallet) · <strong>Amount:</strong> 0 ETH</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <strong>Hex data:</strong><code className="notion-code" style={{ wordBreak: 'break-all' }}>{s.data}</code><Copy text={s.data} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function DelegationCard({ net, lock, sg, info }) {
  const n = NETWORKS[net];
  const [deleg, setDeleg] = useState(null);
  const [err, setErr] = useState(null);
  const indexer = sg.indexer.id;
  useEffect(() => {
    let live = true;
    readDelegation(net, indexer, lock)
      .then((d) => live && setDeleg({ ...d, sgLocked: sg.lockedTokens, sgUntil: sg.lockedUntil }))
      .catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [net, indexer, lock, sg.lockedTokens, sg.lockedUntil]);
  const name = sg.indexer.defaultDisplayName || `${indexer.slice(0, 6)}…${indexer.slice(-4)}`;
  return (
    <div className="wallet-card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Delegated to <strong>{name}</strong> on {n.short}</h3>
      <p><small>Indexer <a href={`${n.explorer}/address/${indexer}`} target="_blank" rel="noreferrer">{indexer}</a>{sg.indexer.url && <> · <a href={sg.indexer.url} target="_blank" rel="noreferrer">{sg.indexer.url}</a></>}</small></p>
      {err && <div className="error-message">On-chain read failed: {err}</div>}
      {deleg && (
        <>
          <div className="notion-fn-grid">
            <div className="notion-fn-item"><code>Delegated shares (on-chain)</code><span>{fmt(deleg.shares)}{deleg.value ? ` ≈ ${fmt(deleg.value)} GRT` : ''}</span></div>
            <div className="notion-fn-item"><code>Undelegating / thawing</code><span>{fmt(deleg.locked ?? BigInt(sg.lockedTokens || 0))} GRT</span></div>
            <div className="notion-fn-item"><code>Unlock epoch</code><span>{(deleg.until ?? BigInt(sg.lockedUntil || 0)) > 0n ? `${deleg.until ?? sg.lockedUntil} (now ${deleg.epoch})` : '—'}</span></div>
            <div className="notion-fn-item"><code>Releasable (vested)</code><span>{fmt(info.releasable)} GRT</span></div>
            <div className="notion-fn-item"><code>Surplus (rewards)</code><span>{fmt(info.surplus)} GRT</span></div>
            <div className="notion-fn-item"><code>Subgraph shares</code><span>{fmt(sg.shareAmount)}{sg.transferredToL2 ? ' · transferred to L2' : ''}</span></div>
          </div>
          <Steps net={net} lock={lock} deleg={deleg} info={info} from={info.beneficiary} />
        </>
      )}
    </div>
  );
}

export default function DelegationHelper({ initialAddress = '', initialNetwork = 'ethereum' }) {
  const [net, setNet] = useState(initialNetwork);
  const [addr, setAddr] = useState(initialAddress);
  const [manualIndexer, setManualIndexer] = useState('');
  const [res, setRes] = useState(null);
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const lookup = useCallback(async (a = addr, network = net) => {
    setErr(null); setRes(null); setNotes([]);
    if (!isAddr(a)) { setErr('Enter your wallet or vesting (lock wallet) address.'); return; }
    setBusy(true);
    const note = [];
    try {
      let wallets = [{ id: a.toLowerCase() }];
      try {
        const r = await resolveLockWallets(ENDPOINTS[network].vesting, a);
        if (r.wallets.length) wallets = r.wallets;
        note.push(r.role === 'beneficiary' ? `This is a beneficiary address with ${r.wallets.length} vesting contract(s).`
          : r.role === 'lock wallet' ? 'This is a vesting (lock wallet) contract.' : 'Not found in the vesting subgraph — treating it as a lock wallet.');
      } catch (e) { note.push(`Vesting subgraph unavailable (${e.message}); treating the address as a lock wallet.`); }

      let stakes = [];
      try {
        const d = await subgraphDelegations(ENDPOINTS[network].network, wallets.map((w) => w.id));
        stakes = d.delegatedStakes;
        note.push(`Graph Network subgraph (block ${d._meta.block.number}) found ${stakes.length} delegation(s).`);
      } catch (e) { note.push(`Graph Network subgraph unavailable (${e.message}). Enter the indexer manually below.`); }
      if (isAddr(manualIndexer)) {
        for (const w of wallets) {
          if (!stakes.some((s) => s.delegator.id === w.id && s.indexer.id === manualIndexer.toLowerCase())) {
            stakes.push({ delegator: { id: w.id }, indexer: { id: manualIndexer.toLowerCase() }, shareAmount: '0', lockedTokens: '0', lockedUntil: 0 });
          }
        }
      }
      const infos = {};
      await Promise.all(wallets.map(async (w) => { infos[w.id] = await readLockWallet(network, w.id); }));
      setRes({ network, wallets, stakes, infos });
    } catch (e) { setErr(e.message); }
    setNotes(note); setBusy(false);
  }, [addr, net, manualIndexer]);

  useEffect(() => { if (isAddr(initialAddress)) { setAddr(initialAddress); setNet(initialNetwork); lookup(initialAddress, initialNetwork); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress, initialNetwork]);

  return (
    <section id="section-delegation" className="notion-section">
      <h2 className="notion-h2">🧊 Undelegate &amp; claim GRT from a vesting contract</h2>
      <div className="notion-callout notion-callout--warning">
        <span className="notion-callout-icon">⚠️</span>
        <div>
          Delegations made <em>from a vesting contract</em> belong to the <strong>lock wallet address</strong>, not your personal wallet — that's why
          Graph Explorer can show nothing. Every transaction below is sent <strong>from your beneficiary wallet, to the lock wallet</strong>, with 0 ETH and the hex data shown.
          Sending to the Staking contract directly fails. {NETWORKS.ethereum.explorerName}/{NETWORKS.arbitrum.explorerName}'s Write tab won't list
          <code className="notion-code"> undelegate</code> — the lock wallet forwards it through its fallback, so use hex data.
        </div>
      </div>

      <div className="search-input-container">
        <select className="search-input" style={{ maxWidth: 220 }} value={net} onChange={(e) => setNet(e.target.value)}>
          <option value="ethereum">{NETWORKS.ethereum.label}</option>
          <option value="arbitrum">{NETWORKS.arbitrum.label}</option>
        </select>
        <input className="search-input" placeholder="Your wallet or vesting contract address" value={addr} onChange={(e) => setAddr(e.target.value.trim())} />
        <button type="button" className="search-button" onClick={() => lookup()} disabled={busy}>{busy ? 'Looking up…' : 'Find my delegations'}</button>
      </div>
      <details><summary><small>Indexer not found automatically? Enter it manually</small></summary>
        <div className="search-input-container">
          <input className="search-input" placeholder="Indexer address (from the lock wallet's delegate tx)" value={manualIndexer} onChange={(e) => setManualIndexer(e.target.value.trim())} />
        </div>
      </details>
      {err && <div className="error-message">{err}</div>}
      {notes.length > 0 && <ul className="notion-text">{notes.map((t) => <li key={t}><small>{t}</small></li>)}</ul>}

      {res && res.wallets.map((w) => {
        const info = res.infos[w.id];
        const mine = res.stakes.filter((s) => s.delegator.id === w.id && (BigInt(s.shareAmount || 0) > 0n || BigInt(s.lockedTokens || 0) > 0n || isAddr(manualIndexer)));
        return (
          <div key={w.id} className="results-container">
            <h3>Vesting contract <a href={`${NETWORKS[res.network].explorer}/address/${w.id}`} target="_blank" rel="noreferrer">{w.id}</a></h3>
            <p><small>Sign with beneficiary: <code className="notion-code">{info.beneficiary || 'unknown (not a lock wallet?)'}</code></small></p>
            {mine.length === 0 && (
              <>
                <p className="notion-text">No active delegation found for this contract on {NETWORKS[res.network].short}. If you think there is one, enter the indexer manually above. You can still claim:</p>
                <Steps net={res.network} lock={w.id} info={info} from={info.beneficiary}
                  deleg={{ indexer: '0x0000000000000000000000000000000000000000', shares: 0n, locked: 0n, until: 0n, epoch: 0n, value: 0n }} />
              </>
            )}
            {mine.map((s) => <DelegationCard key={s.indexer.id} net={res.network} lock={w.id} sg={s} info={info} />)}
          </div>
        );
      })}

      <div className="notion-callout">
        <span className="notion-callout-icon">📖</span>
        <div className="notion-text">
          <strong>How to send a step</strong>
          <ul>
            <li><strong>MetaMask:</strong> Settings → Advanced → turn on <em>Show hex data</em>. Pick the right network and your beneficiary account → Send → To = lock wallet, Amount 0, paste the hex data → Confirm.</li>
            <li><strong>MyEtherWallet:</strong> Contract → Interact with Contract → address = lock wallet, ABI = <code className="notion-code">{'[{"name":"undelegate","type":"function","inputs":[{"name":"_indexer","type":"address"},{"name":"_shares","type":"uint256"}],"outputs":[],"stateMutability":"nonpayable"},{"name":"withdrawDelegated","type":"function","inputs":[{"name":"_indexer","type":"address"},{"name":"_newIndexer","type":"address"}],"outputs":[{"type":"uint256"}],"stateMutability":"nonpayable"}]'}</code></li>
            <li><strong>{NETWORKS.ethereum.explorerName} / {NETWORKS.arbitrum.explorerName}:</strong> only <code className="notion-code">release</code> and <code className="notion-code">withdrawSurplus</code> appear under the lock wallet's <em>Write as Proxy</em> tab. Steps 1–2 need hex data (MetaMask) or MEW.</li>
            <li><strong>cast:</strong> <code className="notion-code">cast send LOCK_WALLET HEX_DATA --rpc-url RPC --ledger</code></li>
          </ul>
          <strong>Order:</strong> 1 undelegate → wait ~28 days (28 epochs) → 2 withdrawDelegated → 3 release → 4 withdrawSurplus. Steps 3–4 alone are enough if nothing is delegated.
          Never use <code className="notion-code">withdraw()</code> — it reverts. Never share your seed phrase; nobody legit DMs you to help.
        </div>
      </div>
    </section>
  );
}
